var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { PRIVATE } from '@/helper/const';
import MediaServices from '@/services/MediaService';
import { GOOGLE_DRIVE, SYSTEM_FOLDER } from '@/util/const_helper';
import { Download01Icon, FolderIcon, DownloadCloud02Icon, Link02Icon, TextInputIcon, Trash01Icon, } from '@gohighlevel/ghl-icons/24/outline';
import { renderIcon, UIActionDropdown, UITable, UITextSmMedium, } from '@gohighlevel/ghl-ui';
import { computed, h, onBeforeMount, onBeforeUnmount, onMounted, ref, } from 'vue';
import { useI18n } from 'vue-i18n';
import 'vue-simple-context-menu/dist/vue-simple-context-menu.css';
import useEmitter from '../composable/useEmitter';
import { useEvent } from '../composable/useEvent';
import { useStore } from '../store';
import { copyPrivateUrl, copyToClipboard, downloadFile, formatBits, formatDate, getDownloadUrl, getFileExtension, } from '../util/helper';
import NoFile from './NoFile.vue';
import TableImage from './TableImage.vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'MyMediaTable',
    props: {
        files: { default: () => [] },
        totalFilesFetched: {},
        count: {},
        type: {}
    },
    emits: ["pageChange"],
    setup(__props, { emit: __emit }) {
        const emitter = useEmitter();
        const store = useStore();
        const props = __props;
        const loading = computed(() => store.loading);
        const emit = __emit;
        const { sendNotification } = useEvent();
        const { t } = useI18n();
        function onActionsClick(e, d) {
            return __awaiter(this, void 0, void 0, function* () {
                if (e === 'getLink') {
                    copyToClipboard(d.url);
                    sendNotification({
                        title: t('common.copiedClipboard'),
                        type: 'success',
                    });
                }
                else if (e === 'delete') {
                    emitter.emit('deleteFileFolder', d);
                }
                else if (e === 'moveFolder') {
                    emitter.emit('moveItem', d);
                }
                else if (e === 'rename') {
                    emitter.emit('renameObject', d);
                }
                else if (e === 'download') {
                    sendNotification({
                        title: t('common.downloadInProgress'),
                        type: 'success',
                    });
                    let fileUrl = d.url;
                    if (d.isPrivate) {
                        const { data: { url }, } = yield MediaServices.getSignedUrl({
                            altId: d.altId,
                            fileId: d._id,
                            contentType: d.contentType,
                            mode: PRIVATE,
                        });
                        fileUrl = url;
                    }
                    const isDownloaded = yield downloadFile({
                        fileUrl,
                        name: d.name,
                        openFileInNewTab: true,
                    });
                    if (!isDownloaded)
                        sendNotification({
                            title: t('common.downloadFailed'),
                            type: 'error',
                        });
                }
                else if (e === 'getPrivateLink') {
                    yield copyPrivateUrl(d);
                    sendNotification({
                        title: t('common.copiedClipboard'),
                        type: 'success',
                    });
                }
                else if (e === 'importToMediaLibrary') {
                    const params = {
                        altId: store.altId,
                        altType: store.altType,
                        url: getDownloadUrl(d),
                    };
                    if (store.activeCategory.includes(GOOGLE_DRIVE)) {
                        params.source = GOOGLE_DRIVE;
                        params.extraParams = {
                            id: `drive-storage/${d._id}`,
                            name: d.name,
                            mimetype: d.contentType,
                            fileExtension: getFileExtension(d.contentType),
                            mode: store.mode,
                            oAuthConnectionId: store.activeGoogleConnection.id,
                        };
                    }
                    sendNotification({
                        title: t('common.importingFiles'),
                        type: 'success',
                    });
                    try {
                        yield MediaServices.addFile(params);
                        sendNotification({
                            title: t('common.successfullyAdded'),
                            type: 'success',
                        });
                    }
                    catch (error) {
                        sendNotification({
                            title: t('common.failedToAddMedia'),
                            type: 'error',
                        });
                    }
                }
            });
        }
        const tableActions = computed(() => {
            return {
                getLink: {
                    label: t('common.getLink'),
                    key: 'getLink',
                    icon: renderIcon(Link02Icon),
                    disabled: store.activeCategory.includes(GOOGLE_DRIVE),
                },
                moveToFolder: {
                    label: t('common.moveToFolder'),
                    key: 'moveFolder',
                    icon: renderIcon(FolderIcon),
                    disabled: store.activeCategory.includes(GOOGLE_DRIVE),
                },
                delete: {
                    label: t('common.deleteFile'),
                    key: 'delete',
                    icon: renderIcon(Trash01Icon),
                    disabled: store.activeCategory.includes(GOOGLE_DRIVE),
                },
                rename: {
                    label: t('common.rename'),
                    key: 'rename',
                    icon: renderIcon(TextInputIcon),
                    disabled: store.activeCategory.includes(GOOGLE_DRIVE),
                },
                _divider: {
                    type: 'divider',
                    key: 3,
                },
                download: {
                    label: t('common.download'),
                    key: 'download',
                    icon: renderIcon(Download01Icon),
                },
                getPrivateLink: {
                    label: t('common.getPrivateLink'),
                    key: 'getPrivateLink',
                    icon: renderIcon(Link02Icon),
                },
                importToMediaLibrary: {
                    label: t('common.importToMediaLibrary'),
                    key: 'importToMediaLibrary',
                    icon: renderIcon(DownloadCloud02Icon),
                },
            };
        });
        const tableCol = [
            {
                type: 'selection',
            },
            {
                title: 'Media',
                key: 'media',
                render(row) {
                    return h(TableImage, {
                        file: row,
                        type: props.type,
                    });
                },
            },
            {
                title: 'Name',
                key: 'name',
                render(row) {
                    return h(UITextSmMedium, {
                        class: 'text-gray-900',
                    }, {
                        default: () => row.name,
                    });
                },
            },
            {
                title: 'Uploaded On',
                key: 'uploadedOn',
                render(row) {
                    return h(UITextSmMedium, {
                        class: 'text-gray-900',
                    }, {
                        default: () => formatDate(row.createdAt),
                    });
                },
            },
            {
                title: 'File size',
                key: 'fileSize',
                render(row) {
                    return h(UITextSmMedium, {
                        class: 'text-gray-900',
                    }, {
                        default: () => formatBits(row.size),
                    });
                },
            },
            {
                title: 'Action',
                key: 'actions',
                render(row) {
                    return h(UIActionDropdown, selectedRows.value.includes(row._id)
                        ? {
                            id: 'my-media-action-disabled',
                        }
                        : {
                            options: tableAction(row),
                            onSelect: val => onActionsClick(val, row),
                            id: 'my-media-action',
                        });
                },
            },
        ];
        const tableAction = row => {
            if (row.isPrivate)
                return [
                    tableActions.value.getPrivateLink,
                    tableActions.value.download,
                    tableActions.value._divider,
                    tableActions.value.delete,
                ];
            if (Object.keys(SYSTEM_FOLDER).includes(row.category)) {
                return [
                    tableActions.value.getLink,
                    tableActions.value.download,
                    tableActions.value._divider,
                    tableActions.value.delete,
                ];
            }
            if (store.mediaModal)
                return [
                    tableActions.value.getLink,
                    tableActions.value.moveToFolder,
                    tableActions.value.rename,
                    tableActions.value._divider,
                    tableActions.value.delete,
                ];
            const defaultActions = [
                tableActions.value.getLink,
                tableActions.value.moveToFolder,
                tableActions.value.rename,
                tableActions.value.download,
                tableActions.value._divider,
                tableActions.value.delete,
            ];
            const googleMediaActions = [...defaultActions];
            googleMediaActions.splice(4, 0, Object.assign({}, tableActions.value.importToMediaLibrary));
            return store.activeCategory.includes(GOOGLE_DRIVE)
                ? googleMediaActions
                : defaultActions;
        };
        function rowKey(row) {
            return row._id;
        }
        const selectedRows = ref();
        const page = ref(1);
        function onSelectedRow(ids) {
            selectedRows.value = [];
            selectedRows.value.push(...ids);
            store.resetSelectedItems();
            const selectedRowsObjects = props.totalFilesFetched.filter(file => {
                if (selectedRows.value.includes(file._id))
                    return {
                        _id: file._id,
                        altId: file.altId,
                        altType: file.altType,
                        parentId: file.parentId,
                        name: file.name,
                        url: file.url,
                        size: file.size,
                        contentType: file.contentType,
                        isPrivate: file.isPrivate,
                        category: file.category,
                    };
            });
            store.setSelectedItems(selectedRowsObjects);
        }
        const pageChange = e => {
            page.value = e;
            emit('pageChange', e - 1);
        };
        onBeforeMount(() => {
            emitter.on('resetCheckedRows', () => (selectedRows.value = []));
            emitter.on('updatePage', response => (page.value = response.page));
        });
        onMounted(() => {
            selectedRows.value = store.selectedItems.map(item => item._id);
        });
        onBeforeUnmount(() => {
            emitter.off('resetCheckedRows');
            emitter.off('updatePage');
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(UITable), {
                id: "my-media-list-view",
                data: _ctx.files,
                columns: tableCol,
                rowKey: rowKey,
                showSizePicker: true,
                checkedRowKeys: selectedRows.value,
                enableTextSearch: false,
                "page-count": Math.ceil(_ctx.count / 10),
                page: page.value,
                loading: loading.value,
                "onUpdate:page": pageChange,
                "onUpdate:checkedRowKeys": onSelectedRow
            }, _createSlots({
                empty: _withCtx(() => [
                    _createVNode(NoFile)
                ]),
                _: 2 /* DYNAMIC */
            }, [
                (_unref(store).activeCategory.includes(_unref(GOOGLE_DRIVE)))
                    ? {
                        name: "pagination",
                        fn: _withCtx(() => []),
                        key: "0"
                    }
                    : undefined
            ]), 1032 /* PROPS, DYNAMIC_SLOTS */, ["data", "checkedRowKeys", "page-count", "page", "loading"]));
        };
    }
});
